import type { Filters, Params } from '~packages/epk-front-shared/api-client';
import { getFilteredData } from '~packages/epk-front-shared/api-client';
import { RequestMethod } from '~packages/json-api/client';
import replacePlaceholdersCustom from '~packages/json-api/client/helpers/__base/replacePlaceholdersCustom';
import type { PathParts } from '~packages/json-api/client/helpers/__base/replacePlaceholdersCustom';

import type {
  ApproveBodyShort,
  DiscardBodyShort,
  IAccessItemApi,
  IAccessNotifyReadReqDto,
  IApproveBody,
  IApproveBodyOnlyOne,
  IApproveBodyWithNew,
  IApproveParts,
  IDiscardBody,
  IDiscardParts,
  IFormApproveOktmosParams,
  IFormRequestOktmosParams,
  IFormRequestOrgsParams,
  IUnapproveBody,
} from '../../dto/access';
import { paramMapToStrDetailed } from '../../lib/paramMapToStrDetailed';
import EpkEntityModuleApiService from '../modules/EpkEntityModuleApiService';

const constructorParams = {
  className: 'AccessApiService',
};

export default class AccessApiService extends EpkEntityModuleApiService {
  constructor() {
    super(constructorParams);
    this.pathFetchAll = '/';
    this.pathFetch = '/one/';
  }

  async getFilteredData(filters: Filters, params: Params) {
    return await getFilteredData(this, filters, params);
  }

  /**
   *
   * @param payload
   * @returns
   */
  async add(payload: IAccessItemApi, params?: { is_approved?: boolean }) {
    return await this._sendRequest(
      '/',
      params,
      { body: JSON.stringify(payload), method: RequestMethod.POST },
      { method: 'add' }
    );
  }

  /**
   *
   * @param point
   * @returns
   */
  async addMSH(point: string) {
    return await this._sendRequest(
      '/send_request_msh_manual',
      { point_code: point },
      { method: RequestMethod.POST },
      { method: 'addMSH' }
    );
  }

  /**
   *
   * @param parts
   * @param payload
   * @returns
   */
  async approve(
    parts: IApproveParts,
    payload?:
      | ApproveBodyShort
      | IApproveBody
      | IApproveBodyOnlyOne
      | IApproveBodyWithNew
  ) {
    return await this._sendRequest(
      replacePlaceholdersCustom(
        '/approve/{code}',
        parts as unknown as PathParts
      ),
      undefined,
      { body: JSON.stringify(payload), method: RequestMethod.PUT },
      { method: 'approve' }
    );
  }

  /**
   *
   * @param parts
   * @param payload
   * @returns
   */
  async discard(
    parts: IDiscardParts,
    payload?: DiscardBodyShort | IDiscardBody
  ) {
    return await this._sendRequest(
      replacePlaceholdersCustom(
        '/discard/{code}',
        parts as unknown as PathParts
      ),
      undefined,
      { body: JSON.stringify(payload), method: RequestMethod.PUT },
      { method: 'discard' }
    );
  }

  /**
   *
   * @param params
   * @returns
   */
  async formApproveOktmos(params: IFormApproveOktmosParams) {
    return await this._sendRequest('/form-approve/oktmos', params, undefined, {
      method: 'formApproveOktmos',
    });
  }

  /**
   *
   * @param params
   * @returns
   */
  async formRequestOktmos(params?: IFormRequestOktmosParams) {
    return await this._sendRequest('/form-request/oktmos', params, undefined, {
      method: 'formRequestOktmos',
    });
  }

  /**
   *
   * @param params
   * @returns
   */
  async formRequestOrgs(params?: IFormRequestOrgsParams) {
    let path = '/form-request/orgs';

    const paramsStr = paramMapToStrDetailed(params);
    if (paramsStr.length) {
      path += '?' + paramsStr;
    }

    return await this._sendRequest(path, undefined, undefined, {
      method: 'formRequestOrgs',
    });
  }

  async generateOrgsList(dto: any) {
    return await this._sendRequest(
      '/generate-orgs-list',
      undefined,
      { body: JSON.stringify(dto), method: RequestMethod.PUT },
      { method: 'generateOrgsList' }
    );
  }

  /**
   *
   * @param params
   * @returns
   */
  async myOrgRights(params?: any) {
    return await this._sendRequest('/my-org-rights', params, undefined, {
      method: 'myOrgRights',
    });
  }

  /**
   *
   * @param dto
   * @returns
   */
  async notifyRead(dto: IAccessNotifyReadReqDto) {
    return await this._sendRequest(
      '/notify-read',
      undefined,
      { body: JSON.stringify(dto), method: RequestMethod.PUT },
      { method: 'notifyRead' }
    );
  }

  async msh(filters: Filters, params: Params) {
    const allParams = { ...params, response_format: 'JSON' };

    return await getFilteredData(
      this,
      filters,
      allParams,
      undefined,
      undefined,
      '/msh'
    );
  }

  /**
   *
   * @returns
   */
  async notifyReadAll() {
    return await this._sendRequest(
      '/notify-read-all',
      undefined,
      { method: RequestMethod.PUT },
      { method: 'notifyReadAll' }
    );
  }

  /**
   *
   * @param params
   * @returns
   */
  async oktmosBusyFree(params?: any) {
    return await this._sendRequest('/oktmos-busy-free', params, undefined, {
      method: 'oktmosBusyFree',
    });
  }

  async oktmosWithOrgs(params?: any) {
    return await this._sendRequest('/oktmos_with_orgs', params, undefined, {
      method: 'oktmosWithOrgs',
    });
  }

  async reportOws(filters: Filters, params: Params) {
    return await getFilteredData(
      this,
      filters,
      params,
      undefined,
      undefined,
      '/report/ows'
    );
  }

  /**
   *
   * @param parts
   * @param payload
   * @returns
   */
  async unapprove(payload: IUnapproveBody) {
    return await this._sendRequest(
      '/unapprove',
      undefined,
      { body: JSON.stringify(payload), method: RequestMethod.PUT },
      { method: 'unapprove' }
    );
  }
}
